import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, TrendUp, Timer, Briefcase, Clock } from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from "recharts";

const AXIS = { fontSize: 11, fill: "#94a3b8" };

function ChartCard({ title, icon: Icon, accent, total, children, testId }) {
  return (
    <Card className="p-5 bg-card border-border" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}>
            <Icon className="h-4 w-4" weight="bold" />
          </div>
          <h3 className="text-base font-bold">{title}</h3>
        </div>
        <span className="text-sm font-black">{total}</span>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </Card>
  );
}

function ChartTooltip({ active, payload, label, currency, unit }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="rounded-md bg-popover border border-border px-3 py-2 text-xs">
      <p className="font-semibold mb-0.5">{label}</p>
      <p className="text-muted-foreground">{unit === "hrs" ? `${v} hrs` : money(v, currency)}</p>
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const currency = user?.currency || "CAD";
  const [year, setYear] = useState(new Date().getFullYear());

  const { data, isLoading } = useQuery({
    queryKey: ["analytics", year],
    queryFn: async () => (await api.get(`/analytics/monthly/${year}`)).data,
  });

  const months = data?.months || [];
  const totals = data?.totals;

  return (
    <div className="animate-fade-up" data-testid="analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline text-primary">Insights</p>
          <h1 className="text-3xl font-black tracking-tight mt-1">Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">Your earnings and hours across the year.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="year-prev" className="rounded-md" onClick={() => setYear(year - 1)}>
            <CaretLeft className="h-4 w-4" weight="bold" />
          </Button>
          <span className="w-16 text-center font-black text-lg" data-testid="analytics-year">{year}</span>
          <Button variant="outline" size="icon" data-testid="year-next" className="rounded-md" onClick={() => setYear(year + 1)}>
            <CaretRight className="h-4 w-4" weight="bold" />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground animate-pulse" data-testid="analytics-loading">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[
              ["Total Income", money(totals.total, currency), "text-primary"],
              ["Overtime", money(totals.overtime, currency), "text-amber-400"],
              ["Paid Duty", money(totals.paid_duty, currency), "text-emerald-400"],
              ["Hours Worked", `${totals.hours} hrs`, "text-blue-400"],
            ].map(([l, v, c]) => (
              <Card key={l} className="p-4 bg-card border-border">
                <p className="overline text-muted-foreground">{l}</p>
                <p className={`text-xl font-black mt-1 ${c}`}>{v}</p>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Total Income by Month" icon={TrendUp} accent="bg-primary/20 text-primary"
              total={money(totals.total, currency)} testId="chart-total-income">
              <AreaChart data={months} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="gTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTooltip currency={currency} />} cursor={{ fill: "#1e293b55" }} />
                <Area type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} fill="url(#gTotal)" />
              </AreaChart>
            </ChartCard>

            <ChartCard title="Hours Worked per Month" icon={Clock} accent="bg-blue-500/20 text-blue-400"
              total={`${totals.hours} hrs`} testId="chart-hours">
              <BarChart data={months} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTooltip currency={currency} unit="hrs" />} cursor={{ fill: "#1e293b55" }} />
                <Bar dataKey="hours" fill="#60a5fa" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>

            <ChartCard title="Monthly Overtime Earnings" icon={Timer} accent="bg-amber-500/20 text-amber-400"
              total={money(totals.overtime, currency)} testId="chart-overtime">
              <BarChart data={months} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTooltip currency={currency} />} cursor={{ fill: "#1e293b55" }} />
                <Bar dataKey="overtime" fill="#fbbf24" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>

            <ChartCard title="Monthly Paid Duty Earnings" icon={Briefcase} accent="bg-emerald-500/20 text-emerald-400"
              total={money(totals.paid_duty, currency)} testId="chart-paid-duty">
              <BarChart data={months} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTooltip currency={currency} />} cursor={{ fill: "#1e293b55" }} />
                <Bar dataKey="paid_duty" fill="#34d399" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}
